/**
 * TanStack Query hooks for the unknown-sender triage Inbox (Phase 8b).
 *
 * Wraps:
 *   GET    /api/v1/admin/inbox?status=...    -> useAdminInboxList
 *   GET    /api/v1/admin/inbox/count         -> useAdminInboxCount
 *   GET    /api/v1/admin/inbox/{id}          -> useAdminInboxRow
 *   POST   /api/v1/admin/inbox/{id}/promote  -> usePromoteInboxRow
 *   POST   /api/v1/admin/inbox/{id}/discard  -> useDiscardInboxRow
 *
 * Count poll runs every 30s so the sidebar badge stays roughly fresh
 * without a websocket subscription; mutations invalidate both the
 * list and the count so the badge reacts immediately on the acting
 * tab.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import api from "@/lib/api";
import type {
  InboxCountResponse,
  InboxDiscardBody,
  InboxMarkSpamBody,
  InboxPromoteResponse,
  InboxRowResponse,
  InboxStatusFilter,
} from "@/types/api";

// Admin-spam blocklist queries get invalidated on mark-spam so the
// /admin/spam page reflects the new pattern immediately if the agent
// jumps over there. The key is duplicated from useAdminSpamBlocks.ts
// rather than imported to keep this hook independent of that module.
const ADMIN_SPAM_QUERY_KEY = ["admin", "spam"] as const;

export const ADMIN_INBOX_QUERY_KEY = ["admin", "inbox"] as const;
export const ADMIN_INBOX_COUNT_QUERY_KEY = ["admin", "inbox", "count"] as const;

export function useAdminInboxList(
  filter: InboxStatusFilter,
  limit = 100,
  offset = 0,
): UseQueryResult<InboxRowResponse[]> {
  return useQuery({
    queryKey: [...ADMIN_INBOX_QUERY_KEY, filter, limit, offset],
    queryFn: async () => {
      const response = await api.get<InboxRowResponse[]>("/admin/inbox", {
        params: { status: filter, limit, offset },
      });
      return response.data;
    },
  });
}

export function useAdminInboxCount(): UseQueryResult<InboxCountResponse> {
  return useQuery({
    queryKey: ADMIN_INBOX_COUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<InboxCountResponse>(
        "/admin/inbox/count",
      );
      return response.data;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}

export function useAdminInboxRow(
  inboxId: string | null | undefined,
): UseQueryResult<InboxRowResponse> {
  return useQuery({
    queryKey: [...ADMIN_INBOX_QUERY_KEY, "row", inboxId],
    queryFn: async () => {
      const response = await api.get<InboxRowResponse>(
        `/admin/inbox/${inboxId}`,
      );
      return response.data;
    },
    enabled: !!inboxId,
  });
}

export function usePromoteInboxRow(): UseMutationResult<
  InboxPromoteResponse,
  Error,
  string
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inboxId: string) => {
      const response = await api.post<InboxPromoteResponse>(
        `/admin/inbox/${inboxId}/promote`,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_INBOX_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: ADMIN_INBOX_COUNT_QUERY_KEY,
      });
      // The promoted row becomes a real ticket; nudge the agent
      // ticket lists so the new ticket appears immediately on a
      // background tab that already has the inbox open.
      void queryClient.invalidateQueries({ queryKey: ["agent", "tickets"] });
    },
  });
}

export function useDiscardInboxRow(): UseMutationResult<
  InboxRowResponse,
  Error,
  { id: string; reason: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const body: InboxDiscardBody = { reason };
      const response = await api.post<InboxRowResponse>(
        `/admin/inbox/${id}/discard`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_INBOX_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: ADMIN_INBOX_COUNT_QUERY_KEY,
      });
    },
  });
}

export function useMarkInboxRowAsSpam(): UseMutationResult<
  InboxRowResponse,
  Error,
  { id: string; reason: string | null }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }) => {
      const body: InboxMarkSpamBody = { reason };
      const response = await api.post<InboxRowResponse>(
        `/admin/inbox/${id}/mark_spam`,
        body,
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_INBOX_QUERY_KEY });
      void queryClient.invalidateQueries({
        queryKey: ADMIN_INBOX_COUNT_QUERY_KEY,
      });
      // Mark-spam adds rows to sender_blocks; invalidate /admin/spam
      // so an open blocklist tab refreshes.
      void queryClient.invalidateQueries({ queryKey: ADMIN_SPAM_QUERY_KEY });
    },
  });
}
